import type { SliderConfig, GaSliderConfig } from './types.js';

// -- DOM helper --
export const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} not found`);
  return el;
};

export const $input = (id: string): HTMLInputElement => {
  const el = $(id);
  if (!(el instanceof HTMLInputElement)) throw new Error(`#${id} is not an input`);
  return el;
};

// -- Core DOM refs --
export const fileInput: HTMLInputElement = $input('fileInput');
export const mainCanvas: HTMLCanvasElement = $('mainCanvas') as HTMLCanvasElement;
export const mainCtx: CanvasRenderingContext2D = mainCanvas.getContext('2d')!;
export const mainSvg: SVGSVGElement = $('mainSvg') as unknown as SVGSVGElement;
export const mainPanel: HTMLElement = $('mainPanel');
export const mainContent: HTMLElement = $('mainContent');
export const seedMarker: HTMLElement = $('seedMarker');
export const placeholder: HTMLElement = $('placeholder');
export const statusBar: HTMLElement = $('statusBar');
export const exportBtn: HTMLButtonElement = $('exportBtn') as HTMLButtonElement;
export const copyBtn: HTMLButtonElement = $('copyBtn') as HTMLButtonElement;
export const stashBtn: HTMLButtonElement = $('stashBtn') as HTMLButtonElement;
export const stashBar: HTMLElement = $('stashBar');
export const stashItems: HTMLElement = $('stashItems');
export const clearStashBtn: HTMLButtonElement = $('clearStashBtn') as HTMLButtonElement;
export const refitBtn: HTMLButtonElement = $('refitBtn') as HTMLButtonElement;
export const semiManualBtn: HTMLInputElement = $('semiManualBtn') as HTMLInputElement;

export const geneticBar: HTMLElement = $('geneticBar');
export const gaFitBtn: HTMLButtonElement = $('gaFitBtn') as HTMLButtonElement;
export const paintBtn: HTMLInputElement = $('paintBtn') as HTMLInputElement;
export const paintBar: HTMLElement = $('paintBar');
export const brushSizeEl: HTMLInputElement = $input('brushSize');
export const brushSizeVal: HTMLElement = $('brushSizeVal');
export const paintColorPreview: HTMLElement = $('paintColorPreview');
export const pickColorBtn: HTMLButtonElement = $('pickColorBtn') as HTMLButtonElement;
export const paintModeBtn: HTMLButtonElement = $('paintModeBtn') as HTMLButtonElement;
export const eraseModeBtn: HTMLButtonElement = $('eraseModeBtn') as HTMLButtonElement;
export const resetPaintBtn: HTMLButtonElement = $('resetPaintBtn') as HTMLButtonElement;
export const paintCursorSvg: SVGSVGElement = $('paintCursorSvg') as unknown as SVGSVGElement;
export const paintCursorCircle: SVGCircleElement = $('paintCursorCircle') as unknown as SVGCircleElement;
export const paintCursorBg: SVGCircleElement = $('paintCursorBg') as unknown as SVGCircleElement;
export const paintCursorFill: SVGCircleElement = $('paintCursorFill') as unknown as SVGCircleElement;
export const paintCanvas: HTMLCanvasElement = $('paintCanvas') as HTMLCanvasElement;
export const paintCtx: CanvasRenderingContext2D = paintCanvas.getContext('2d')!;
export const modeBtn: HTMLButtonElement = $('modeBtn') as HTMLButtonElement;
export const toast: HTMLElement = $('toast');
export const nodesSvg: SVGSVGElement = $('nodesSvg') as unknown as SVGSVGElement;

// -- Slider configs --
export const sliders: Record<string, SliderConfig> = {
  tolerance: { el: $input('tolerance'),  val: $('toleranceVal'),  fmt: (v: string) => parseFloat(v).toFixed(1) },
  margin:    { el: $input('margin'),     val: $('marginVal'),     fmt: (v: string) => v },
  rdp:       { el: $input('rdpEpsilon'), val: $('rdpVal'),        fmt: (v: string) => parseFloat(v).toFixed(1) },
  segments:  { el: $input('segments'),   val: $('segmentsVal'),   fmt: (v: string) => v === '161' ? '\u221E' : v },
  opacity:   { el: $input('overlayOpacity'), val: $('opacityVal'), fmt: (v: string) => parseFloat(v).toFixed(2) },
};

export const gaSliders: Record<string, GaSliderConfig> = {
  penalty:   { el: $input('gaPenalty'),   val: $('gaPenaltyVal'),   fmt: (v: string) => v },
  overshoot: { el: $input('gaOvershoot'), val: $('gaOvershootVal'), fmt: (v: string) => (parseInt(v) / 10).toFixed(1) },
  pop:       { el: $input('gaPop'),       val: $('gaPopVal'),       fmt: (v: string) => v },
  gen:       { el: $input('gaGen'),       val: $('gaGenVal'),       fmt: (v: string) => v },
};

// -- Persistence constants --
export const IDB_NAME: string = 'svgFitDB';
export const IDB_VER: number = 1;
export const LS_KEY: string = 'svgFitState';
